import React, {MouseEventHandler, useEffect, useRef, useState} from 'react';

import jquery from 'jquery';
import Slider, {SliderSettings} from 'react-slick';
import styled from 'styled-components';

import {StyledStepContentSeparatorDiv} from 'app/procedures/components/structures';
import {StyledDiffedImageContainerDiv} from 'app/procedures/styles';
import {useAccountSettingsState} from 'app/settings/api';
import {gt} from 'app/utils/i18n';
import {markAsSafe, safeCdnImageRef} from 'app/utils/strings';

import {EmbedlyVideoThumbnail} from './EmbedlyVideoThumbnail';

import type {AttachedEmbed, AttachedMedia} from 'sweet-types';

type AttachedMediaWithChangedState =
  | AttachedMedia
  | (AttachedMedia & {changedState?: string})
  | AttachedEmbed
  | (AttachedEmbed & {changedState?: string});

interface Props {
  images: Array<AttachedMediaWithChangedState>;
  config?: SliderSettings;
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

export function ImagesCarousel({images, config, ...other}: Props): JSX.Element {
  const {MEDIA_URL_READ} = useAccountSettingsState().data!;

  // used to force re-render
  const [updateCount, setUpdateCount] = useState(0);

  const elementRef = useRef<HTMLDivElement>(null);
  const slickRef = useRef<Slider>(null);

  useEffect(() => {
    if (elementRef.current != null) {
      // hack to update slider height after the first carousel image is loaded
      jquery('.slick-active img:not(.loaded)', elementRef.current).on(
        'load',
        (ev) => {
          jquery(ev.target).addClass('loaded');
          setUpdateCount(updateCount + 1);
        }
      );
    }
  }, []);

  function buildThumb(index: number): JSX.Element {
    const image = images[index];
    if ('image_ref' in image && 'embed_thumb' in image) {
      const {image_ref, embed_thumb} = image;

      let imageURL = haveContent(image_ref)
        ? safeCdnImageRef(image_ref, MEDIA_URL_READ, 'small')
        : haveContent(embed_thumb)
        ? embed_thumb
        : '';

      return (
        <StyledThumbnailButton>
          <img src={imageURL} />
        </StyledThumbnailButton>
      );
    } else {
      return <div />;
    }
  }

  function resizeContainer() {
    if (slickRef.current != null) {
      slickRef.current.innerSlider.adaptHeight();
    }
  }

  function renderImage(
    image: AttachedMediaWithChangedState,
    index: number
  ): JSX.Element {
    if ('image_ref' in image && haveContent(image.image_ref)) {
      const imageURL = image.image_ref;
      return (
        <StyledImageContainerDIV
          key={imageURL}
          className={'changedState' in image ? image.changedState : ''}
          data-thumb={imageURL}
        >
          <img src={safeCdnImageRef(imageURL, MEDIA_URL_READ, 'large')} />
        </StyledImageContainerDIV>
      );
    } else if ('embed_ref' in image && haveContent(image.embed_ref)) {
      return (
        <StyledImageContainerDIV
          key={image.embed_ref}
          className={'changedState' in image ? image.changedState : ''}
          data-thumb={image.embed_thumb}
        >
          <EmbedlyVideoThumbnail video={image} onPlay={resizeContainer} />
        </StyledImageContainerDIV>
      );
    } else if ('embed_html' in image && haveContent(image.embed_html)) {
      return (
        <StyledImageContainerDIV
          key={image.embed_html + index.toString()}
          className={'changedState' in image ? image.changedState : ''}
        >
          <div dangerouslySetInnerHTML={markAsSafe(image.embed_html)} />
        </StyledImageContainerDIV>
      );
    } else {
      return (
        <div className="text-warning p-2">
          <i className="far fa-fw fa-exclamation-triangle"></i>
          {gt('Cannot display the content of the embedded element.')}
        </div>
      );
    }
  }

  const settings: SliderSettings = {
    adaptiveHeight: true,
    autoplay: false,
    dots: true,
    infinite: true,
    customPaging: buildThumb,
    ...config,
  };

  return (
    <StyledStepContentSeparatorDiv {...other} ref={elementRef}>
      {images.length > 1 ? (
        <Slider {...settings} ref={slickRef}>
          {images.filter((c) => c).map(renderImage)}
        </Slider>
      ) : (
        renderImage(images[0], 0)
      )}
    </StyledStepContentSeparatorDiv>
  );
}

const StyledThumbnailButton = styled.button`
  img {
    border: 1px solid #dadada;
    width: auto;
    max-width: 99%;
  }
`;
const StyledImageContainerDIV = styled(StyledDiffedImageContainerDiv)`
  text-align: center;

  > img {
    display: inline-block !important;
    max-width: 100%;
  }
`;

function haveContent(val: any): val is string {
  return typeof val === 'string' && Boolean(val) && val !== '';
}
